import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TeamUniverse = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('team-universe');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const teamMembers = [
    // {
    //   id: 1,
    //   name: "Siddhant Arya",
    //   role: "Founder & CEO",
    //   avatar: "/siddhant.jpg",
    //   bio: "Visionary leader with 5+ years in software development. Passionate about transforming complex ideas into elegant digital solutions.",
    //   experience: "5+ years",
    //   projects: "50+"
    // },
    {
      id: 2,
      name: "Sudhakar Pandey",
      role: "Backend Developer",
      avatar: "/sudhakar.jpeg",
      bio: "Experienced in the development of scalable projects having huge userbase, with highest level of optimizations.",
      experience: "3+ years",
      projects: "10+"
    },
    {
      id: 3,
      name: "Noor Uddin Sheikh",
      role: "Frontend Developer",
      avatar: "/noor.jpeg",
      bio: "Human-centered design advocate who ensures every pixel serves a purpose. Creates interfaces that feel intuitive and delightful.",
      experience: "3+ years",
      projects: "23+"
    },
    {
      id: 4,
      name: "Shrishtika Rai",
      role: "Frontend Developer",
      avatar: "/shristika.png",
      bio: "Frontend development with top notch UI/UX experience, with high optimizations, experienced in developing scalable projects.",
      experience: "1+ years",
      projects: "28+"
    },
    {
      id: 5,
      name: "Aviral Saxena",
      role: "QA",
      avatar: "/aviral.jpg",
      bio: "Ensuring quality for the products being delivered with expertise in automation testing using python, java, selenium.",
      experience: "2+ years",
      projects: "10+"
    },
    {
      id: 6,
      name: "Taffazzul bin Azam",
      role: "Frontend Developer",
      avatar: "/taffazzul.jpg",
      bio: "Experienced Professional in developing enterprise level frontend, with over 15+ projects including ERP dashboards and task management tools.",
      experience: "2+ years",
      projects: "15+"
    },
    {
      id: 7,
      name: "Manpreet Kaur",
      role: "Frontend Developer",
      avatar: "/manpreet.jpg",
      bio: "Frontend Developer with 2+ years of experience and 10+ projects, specializing in React and modern web technologies to build fast, responsive, and engaging user interfaces.",
      experience: "2+ years",
      projects: "10+"
    }
  ];

  return (
    <section id="team-universe" className="py-12 md:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center space-x-2 bg-accent/10 px-4 py-2 rounded-full mb-4">
            <Icon name="Users" size={16} className="text-accent" />
            <span className="text-sm font-medium text-accent">Meet Our Team</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Team Universe
          </h2>
          <p className="text-base md:text-xl text-muted-foreground max-w-3xl mx-auto">
            The brilliant minds behind every pixel, every line of code, and every innovative solution.
          </p>
        </div>

        {/* Team Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8 md:mb-12">
          {teamMembers.map((member) => (
            <div
              key={member.id}
              className="bg-card border border-border rounded-xl p-6"
            >
              <div className="flex flex-col items-center text-center">
                <div className="relative mb-4">
                  <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-accent/20">
                    <Image
                      src={member.avatar}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                <h3 className="text-lg font-bold text-foreground mb-1">{member.name}</h3>
                <p className="text-accent font-medium text-sm mb-3">{member.role}</p>
                <p className="text-muted-foreground text-sm leading-relaxed mb-4">{member.bio}</p>

                <div className="flex space-x-4 w-full justify-center pt-4 border-t border-border">
                  <div className="text-center">
                    <div className="text-base font-bold text-foreground">{member.experience}</div>
                    <div className="text-xs text-muted-foreground">Experience</div>
                  </div>
                  <div className="text-center">
                    <div className="text-base font-bold text-foreground">{member.projects}</div>
                    <div className="text-xs text-muted-foreground">Projects</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Team Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 bg-card/50 border border-border rounded-xl p-6 md:p-8 mb-8 md:mb-12">
          {[
            { label: "Team Members", value: "15+", icon: "Users" },
            { label: "Combined Experience", value: "17+ years", icon: "Clock" },
            { label: "Projects Delivered", value: "40+", icon: "Briefcase" },
            { label: "Technologies Mastered", value: "40+", icon: "Code" }
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="inline-flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-accent/10 rounded-xl mb-3">
                <Icon name={stat.icon} size={20} className="text-accent" />
              </div>
              <div className="text-xl md:text-2xl font-bold text-foreground mb-1">{stat.value}</div>
              <div className="text-xs md:text-sm text-muted-foreground">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Join CTA */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-accent/10 to-success/10 border border-accent/20 rounded-xl p-6 md:p-8">
            <Icon name="Rocket" size={40} className="text-accent mx-auto mb-4" />
            <h3 className="text-xl md:text-2xl font-bold text-foreground mb-3">
              Ready to Join Our Universe?
            </h3>
            <p className="text-sm md:text-base text-muted-foreground mb-4 max-w-2xl mx-auto">
              We're always looking for talented individuals who share our passion for innovation and excellence.
            </p>
            <button className="inline-flex items-center space-x-2 bg-accent text-accent-foreground px-6 py-3 rounded-lg font-medium">
              <Icon name="Mail" size={18} />
              <span>View Open Positions</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TeamUniverse;